import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EmptyState from '@/components/base/EmptyState';
import { apiRequest } from '@/services/appApi';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';

interface NotifItem {
  id: string;
  type: 'signal' | 'stoploss' | 'takeprofit' | 'agent' | 'system' | 'risk';
  title: string;
  body: string;
  pair?: string;
  pnl?: number;
  read: boolean;
  created_at: string;
}

interface NotificationApiItem {
  notification_id: string;
  event_type: string;
  title: string;
  body: string;
  read: boolean;
  created_at: number;
}

const typeConfig: Record<NotifItem['type'], { icon: string; color: string; bg: string; label: string }> = {
  signal:      { icon: 'ri-signal-tower-line',          color: '#6366f1', bg: '#eef2ff', label: '信号' },
  stoploss:    { icon: 'ri-shield-flash-line',           color: '#EF4444', bg: '#FEF2F2', label: '止损' },
  takeprofit:  { icon: 'ri-money-dollar-circle-line',    color: '#10B981', bg: '#ECFDF5', label: '止盈' },
  agent:       { icon: 'ri-robot-2-line',                color: '#3B82F6', bg: '#EFF6FF', label: 'AI 智能体' },
  system:      { icon: 'ri-notification-4-line',         color: '#F59E0B', bg: '#FFFBEB', label: '系统' },
  risk:        { icon: 'ri-error-warning-line',          color: '#ef4444', bg: '#fff7ed', label: '风险' },
};

const filterTabs = [
  { label: '全部', types: null },
  { label: '信号', types: ['signal'] },
  { label: '止损/盈', types: ['stoploss', 'takeprofit'] },
  { label: '风险', types: ['risk'] },
  { label: 'AI 智能体', types: ['agent'] },
  { label: '系统', types: ['system'] },
];

const PAGE_SIZE = 10;

function mapType(eventType: string): NotifItem['type'] {
  if (eventType.includes('risk')) return 'risk';
  if (eventType.includes('signal')) return 'signal';
  if (eventType.includes('takeprofit')) return 'takeprofit';
  if (eventType.includes('stoploss')) return 'stoploss';
  if (eventType.includes('agent') || eventType.includes('follow')) return 'agent';
  return 'system';
}

function mapNotification(item: NotificationApiItem): NotifItem {
  return {
    id: item.notification_id,
    type: mapType(item.event_type),
    title: item.title,
    body: item.body,
    read: item.read,
    created_at: new Date(item.created_at * 1000).toISOString(),
  };
}

function formatTime(iso: string) {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diff < 1) return '刚刚';
  if (diff < 60) return `${diff}分钟前`;
  if (diff < 1440) return `${Math.floor(diff / 60)}小时前`;
  return `${Math.floor(diff / 1440)}天前`;
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { refreshUnread } = useNotification();
  const [items, setItems] = useState<NotifItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeFilter, setActiveFilter] = useState<typeof filterTabs[0]>(filterTabs[0]);

  const fetchPage = useCallback(async (nextPage: number, append: boolean) => {
    if (!accessToken) {
      setItems([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const data = await apiRequest<{ items: NotificationApiItem[]; total: number; page: number; has_more: boolean }>(
        `/app/notifications?page=${nextPage}&page_size=${PAGE_SIZE}`,
        {},
        accessToken,
      );
      const mapped = data.items.map(mapNotification);
      setTotal(data.total);
      setPage(nextPage);
      setItems(prev => append ? [...prev, ...mapped.filter(item => !prev.some(existing => existing.id === item.id))] : mapped);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  const hasMore = items.length < total;

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) fetchPage(page + 1, true);
  }, [fetchPage, hasMore, loadingMore, page]);

  useEffect(() => {
    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const totalH = document.documentElement.scrollHeight;
      if (totalH - scrolled < 100) handleLoadMore();
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [handleLoadMore]);

  const filteredItems = useMemo(
    () => activeFilter.types ? items.filter(i => (activeFilter.types as string[]).includes(i.type)) : items,
    [activeFilter.types, items],
  );

  const unreadCount = items.filter(i => !i.read).length;
  const riskCount = items.filter(i => i.type === 'risk' && !i.read).length;

  const markAllRead = async () => {
    if (!accessToken) return;
    await apiRequest('/app/notifications/read-all', { method: 'POST' }, accessToken);
    setItems(prev => prev.map(i => ({ ...i, read: true })));
    refreshUnread();
  };

  const markRead = async (id: string) => {
    if (!accessToken) return;
    setItems(prev => prev.map(i => i.id === id ? { ...i, read: true } : i));
    await apiRequest(`/app/notifications/${id}/read`, { method: 'PATCH' }, accessToken);
    refreshUnread();
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      <div className="bg-white border-b border-gray-100 px-4 pb-0" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 48px)' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center">
              <i className="ri-arrow-left-s-line text-gray-600 text-xl" />
            </button>
            <div>
              <h1 className="text-gray-900 font-bold text-lg">通知中心</h1>
              {unreadCount > 0 && (
                <p className="text-xs mt-0.5">
                  <span className="text-indigo-500">{unreadCount} 条未读</span>
                  {riskCount > 0 && <span className="text-red-500 ml-2">· {riskCount} 条风险预警</span>}
                </p>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="text-indigo-500 text-xs font-medium px-3 py-1.5 bg-indigo-50 rounded-full">
              全部已读
            </button>
          )}
        </div>

        <div className="flex gap-2 overflow-x-auto pb-3" style={{ scrollbarWidth: 'none' }}>
          {filterTabs.map(tab => (
            <button
              key={tab.label}
              onClick={() => setActiveFilter(tab)}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
              style={activeFilter.label === tab.label
                ? { background: '#6366f1', color: 'white' }
                : { background: '#F3F4F6', color: '#6B7280' }}
            >
              {tab.label}
              {tab.label === '风险' && riskCount > 0 && <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1">{riskCount}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4">
        {riskCount > 0 && (activeFilter.label === '全部' || activeFilter.label === '风险') && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-3.5 mb-3 flex gap-2.5">
            <i className="ri-error-warning-fill text-red-500 text-lg flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-700 font-semibold text-sm">有 {riskCount} 条风险预警需要处理</p>
              <p className="text-red-500 text-xs mt-0.5">请及时查看并处理，避免损失扩大</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col gap-3 mt-2">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="bg-white rounded-2xl h-20 animate-pulse" />)}
          </div>
        ) : filteredItems.length === 0 ? (
          <EmptyState
            icon="ri-notification-off-line"
            title={activeFilter.label === '全部' ? '暂无通知' : `暂无${activeFilter.label}类通知`}
            subtitle="通知会推送 AI 智能体的交易信号、止损止盈触发、风险预警和系统消息。"
            primaryAction={activeFilter.label === '全部' ? {
              label: '查看交易记录',
              icon: 'ri-file-list-3-line',
              onClick: () => navigate('/trade-history'),
            } : undefined}
            footer="重要风险预警会优先提醒"
          />
        ) : (
          <div className="flex flex-col gap-2.5 mt-1">
            {filteredItems.map((item) => {
              const cfg = typeConfig[item.type];
              const isRisk = item.type === 'risk';
              return (
                <div
                  key={item.id}
                  onClick={() => markRead(item.id)}
                  className="bg-white rounded-2xl px-4 py-3.5 cursor-pointer active:scale-98 transition-transform"
                  style={{
                    boxShadow: isRisk && !item.read ? '0 2px 10px rgba(239,68,68,0.1)' : '0 2px 10px rgba(0,0,0,0.05)',
                    borderLeft: !item.read ? `3px solid ${isRisk ? '#ef4444' : '#6366f1'}` : '3px solid transparent',
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: cfg.bg }}>
                      <i className={`${cfg.icon} text-lg`} style={{ color: cfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className={`text-sm font-semibold leading-snug ${item.read ? 'text-gray-600' : 'text-gray-900'}`}>
                          {item.title}
                        </p>
                        {!item.read && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: isRisk ? '#ef4444' : '#6366f1' }} />}
                      </div>
                      <p className="text-gray-400 text-xs leading-relaxed line-clamp-2">{item.body}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="text-gray-300 text-xs">{formatTime(item.created_at)}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded font-medium" style={{ background: cfg.bg, color: cfg.color }}>
                          {cfg.label}
                        </span>
                        {item.pair && <span className="bg-gray-100 text-gray-500 text-xs px-1.5 py-0.5 rounded font-medium">{item.pair}</span>}
                        {item.pnl != null && (
                          <span className={`text-xs font-bold ${item.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {item.pnl >= 0 ? '+' : ''}${item.pnl.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {loadingMore && (
              <div className="flex justify-center py-4">
                <span className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {!hasMore && filteredItems.length > 0 && <p className="text-center text-gray-300 text-xs py-4">— 已加载全部通知 —</p>}
          </div>
        )}
      </div>
    </div>
  );
}
